import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { MultiSelect, MultiSelectOption } from '@/components/ui/multi-select';
import { RecipientGroup } from '@/types';
import { Send, X, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface AnnouncementFormProps {
  recipientOptions: MultiSelectOption[];
  onSend: (message: string, sendTo: RecipientGroup[], includeRsvpLink: boolean, includePaymentLink: boolean) => Promise<void>;
  sending?: boolean;
  sendProgress?: { sent: number; total: number } | null;
  defaultMessage?: string;
  defaultSendTo?: RecipientGroup[];
  defaultIncludeRsvpLink?: boolean;
  defaultIncludePaymentLink?: boolean;
  showRsvpLinkOption?: boolean;
  submitButtonText?: string;
}

export default function AnnouncementForm({
  recipientOptions,
  onSend,
  sending = false,
  sendProgress = null,
  defaultMessage = '',
  defaultSendTo = [],
  defaultIncludeRsvpLink = false,
  defaultIncludePaymentLink = false,
  showRsvpLinkOption = true,
  submitButtonText = 'Send Announcement',
}: AnnouncementFormProps) {
  const [message, setMessage] = useState(defaultMessage);
  const [sendTo, setSendTo] = useState<RecipientGroup[]>(defaultSendTo);
  const [includeRsvpLink, setIncludeRsvpLink] = useState(defaultIncludeRsvpLink);
  const [includePaymentLink, setIncludePaymentLink] = useState(defaultIncludePaymentLink);
  const [error, setError] = useState<string | null>(null);
  const [lastFailedMessage, setLastFailedMessage] = useState<{
    message: string;
    sendTo: RecipientGroup[];
    includeRsvpLink: boolean;
    includePaymentLink: boolean;
  } | null>(null);

  const getRecipientCount = (): number => {
    if (sendTo.length === 0) return 0;

    const allOption = recipientOptions.find((opt) => opt.value === 'all');
    if (sendTo.includes('all') && allOption) {
      return allOption.count || 0;
    }

    let total = 0;
    sendTo.forEach((group) => {
      const option = recipientOptions.find((opt) => opt.value === group);
      if (option) {
        total += option.count || 0;
      }
    });

    return total;
  };

  const handleSubmit = async () => {
    setError(null);
    try {
      await onSend(message.trim(), sendTo, includeRsvpLink, includePaymentLink);
      // Reset form on success
      setMessage('');
      setSendTo([]);
      setIncludeRsvpLink(false);
      setIncludePaymentLink(false);
      setLastFailedMessage(null);
    } catch (err: any) {
      // Save the message for retry
      setLastFailedMessage({
        message: message.trim(),
        sendTo: [...sendTo],
        includeRsvpLink,
        includePaymentLink,
      });

      // Set error message based on error type
      if (err.code === 'permission-denied') {
        setError('Permission denied. You may not have access to send announcements.');
      } else if (err.code === 'unauthenticated') {
        setError('You must be logged in to send announcements.');
      } else if (err.code === 'resource-exhausted') {
        setError('Rate limit exceeded. Please try again later.');
      } else if (err.message?.includes('network') || err.message?.includes('Network')) {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError('Error sending announcement. Please try again.');
      }
    }
  };

  const handleRetry = async () => {
    if (!lastFailedMessage) return;

    setError(null);
    setMessage(lastFailedMessage.message);
    setSendTo(lastFailedMessage.sendTo);
    setIncludeRsvpLink(lastFailedMessage.includeRsvpLink);
    setIncludePaymentLink(lastFailedMessage.includePaymentLink);
    setLastFailedMessage(null);

    try {
      await onSend(
        lastFailedMessage.message,
        lastFailedMessage.sendTo,
        lastFailedMessage.includeRsvpLink,
        lastFailedMessage.includePaymentLink
      );
      // Reset form on success
      setMessage('');
      setSendTo([]);
      setIncludeRsvpLink(false);
      setIncludePaymentLink(false);
    } catch (err: any) {
      // Keep the failed message for another retry
      setLastFailedMessage({
        message: lastFailedMessage.message,
        sendTo: lastFailedMessage.sendTo,
        includeRsvpLink: lastFailedMessage.includeRsvpLink,
        includePaymentLink: lastFailedMessage.includePaymentLink,
      });

      if (err.code === 'permission-denied') {
        setError('Permission denied. You may not have access to send announcements.');
      } else if (err.code === 'unauthenticated') {
        setError('You must be logged in to send announcements.');
      } else if (err.code === 'resource-exhausted') {
        setError('Rate limit exceeded. Please try again later.');
      } else if (err.message?.includes('network') || err.message?.includes('Network')) {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError('Error sending announcement. Please try again.');
      }
    }
  };

  const recipientCount = getRecipientCount();

  return (
    <div className="space-y-4">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <span>{error}</span>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={() => setError(null)}
            >
              <X className="h-4 w-4" />
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {lastFailedMessage && !error && (
        <Alert>
          <AlertDescription className="flex items-center justify-between">
            <span>Previous message failed to send.</span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRetry}
            >
              Resend
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <div>
        <Label htmlFor="announcement-message">Message</Label>
        <Textarea
          id="announcement-message"
          placeholder="Enter your announcement message..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={6}
          className="mt-1.5"
        />
      </div>

      <div>
        <Label htmlFor="send-to">Send To</Label>
        <MultiSelect
          options={recipientOptions}
          value={sendTo}
          onChange={(value) => setSendTo(value as RecipientGroup[])}
          placeholder="Select recipient groups..."
          className="mt-1.5"
        />
      </div>

      {showRsvpLinkOption && (
        <div className="flex items-center space-x-2">
          <Checkbox
            id="include-rsvp-link"
            checked={includeRsvpLink}
            onChange={(e) => setIncludeRsvpLink(e.target.checked)}
          />
          <Label
            htmlFor="include-rsvp-link"
            className="text-sm font-normal cursor-pointer"
          >
            Include RSVP link in email
          </Label>
        </div>
      )}

      <div className="flex items-center space-x-2">
        <Checkbox
          id="include-payment-link"
          checked={includePaymentLink}
          onChange={(e) => setIncludePaymentLink(e.target.checked)}
        />
        <Label
          htmlFor="include-payment-link"
          className="text-sm font-normal cursor-pointer"
        >
          Include payment link (shows outstanding balance if applicable)
        </Label>
      </div>

      {sending && sendProgress ? (
        <div
          className="flex items-center justify-center gap-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3"
          role="status"
          aria-live="polite"
        >
          <div className="h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <span className="text-base font-semibold tabular-nums">
            {sendProgress.sent} / {sendProgress.total}
          </span>
          <span className="text-sm text-muted-foreground">emails sent</span>
        </div>
      ) : null}

      <div className="flex items-center justify-between pt-2">
        <div className="text-sm text-muted-foreground">
          {recipientCount > 0 && `Will send to ${recipientCount} recipient(s)`}
        </div>
        <Button
          onClick={handleSubmit}
          disabled={sending || !message.trim() || sendTo.length === 0}
        >
          <Send className="mr-2 h-4 w-4" />
          {sending && sendProgress
            ? `${sendProgress.sent} / ${sendProgress.total}`
            : sending
              ? 'Sending...'
              : submitButtonText}
        </Button>
      </div>
    </div>
  );
}

